import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { routes } from "../../../configs/constants/routes";
import ExpandToggle from "../../Core/ExpandToggle/ExpandToggle";
import { customerRoute, formatRoute } from "../../../utils/route.utils";
import { createInvoiceBatchIssueDetail } from "../../../utils/api.utils";

export interface BatchIssue {
	code: string;
	timeReport?: {
		id: string;
		/** Start of reported time, missing on batches created before enrichment was added */
		startDate?: string;
		consultantName?: string;
	};
	customer?: { id: string };
	/** Invoice batches: the customer field a DELIVERY_METHOD_INCOMPLETE customer lacks */
	field?: string;
	consultant?: { id: string };
	previewError?: {
		code?: string;
		title?: string;
		detail?: string;
	};
	/** Invoice batches: set when the invoice template left the customer out */
	skip?: {
		reason: string;
		timeReportCount: number;
	};
}

/**
 * Codes with an explanatory text under `<issueNamespace>.issueDescription.<code>`,
 * rendered below the issue's label.
 */
const CODES_WITH_DESCRIPTION: Partial<Record<Props["issueNamespace"], string[]>> = {};

interface Props {
	errors: BatchIssue[];
	warnings: BatchIssue[];
	/** Locale namespace holding issue, issueInfo and issueType.* texts */
	issueNamespace: "invoiceBatch" | "salaryBatch";
	/**
	 * Period the batch covers, used to build time report deep links.
	 * Invoice batches have no lower bound; leave `start` unset and the
	 * issue's own startDate is used instead.
	 */
	period: { start?: Date | string; end: Date | string };
}

/**
 * Summary boxes for the outcome of an invoice or salary batch preview.
 *
 * Errors (red) block or drop whole invoices/payslips and keep the original
 * "could not be included" copy. Warnings (amber) mean something was LEFT OUT
 * of the run — e.g. an unattested time report — while the rest of the
 * customer's invoice still exists, so they get their own honest wording
 * instead of implying the invoice itself was excluded.
 */
const BatchWarningSummary: React.FC<Props> = ({ errors, warnings, issueNamespace, period }) => {
	const { t } = useTranslation();
	if (!errors.length && !warnings.length) {
		return null;
	}
	function getLink(issue: BatchIssue) {
		if (issue.timeReport) {
			const start = period.start || issue.timeReport.startDate;

			if (!start) return null;

			return (
				routes.TIME_REPORTS +
				`?highlight=${issue.timeReport.id}&start=${dayjs(start).format("YYYY-MM-DD")}&end=${dayjs(
					period.end
				).format("YYYY-MM-DD")}`
			);
		} else if (issue.customer) {
			return customerRoute(issue.customer.id);
		} else if (issue.consultant) {
			return formatRoute(routes.CONSULTANTS_DETAILS + "/" + routes.CONSULTANTS_PROFILE, [
				issue.consultant.id,
			]);
		}
		return null;
	}

	function getLabel(issue: BatchIssue) {
		const base = t((issueNamespace + ".issueType." + issue.code) as any);

		const details = [
			issue.timeReport?.consultantName,
			issue.timeReport?.startDate && dayjs(issue.timeReport.startDate).format("YYYY-MM-DD"),
			issue.previewError?.detail,
			issue.skip?.reason,
			createInvoiceBatchIssueDetail(issue),
		]
			.filter(Boolean)
			.join(", ");

		return details ? `${base}: ${details}` : base;
	}

	/** Muted line under the label: an explanatory text for the code and/or the skipped time report count. */
	function getDescription(issue: BatchIssue) {
		return [
			CODES_WITH_DESCRIPTION[issueNamespace]?.includes(issue.code) &&
				t((issueNamespace + ".issueDescription." + issue.code) as any),
			issue.skip &&
				t((issueNamespace + ".skippedTimeReports") as any, { count: issue.skip.timeReportCount }),
		]
			.filter(Boolean)
			.join(" · ");
	}
	const renderIssueList = (issues: BatchIssue[]) => (
		<ul className="ml-4 list-disc">
			{issues.map((issue, i) => {
				const link = getLink(issue);
				const label = getLabel(issue);
				const description = getDescription(issue);
				return (
					<li key={i}>
						{link ? (
							<Link className="underline" to={link} target="_blank">
								{label}
							</Link>
						) : (
							label
						)}
						{description && <p className="text-sm text-gray-600">{description}</p>}
					</li>
				);
			})}
		</ul>
	);

	return (
		<div className="mb-3 flex flex-col gap-3">
			{errors.length > 0 && (
				<IssueBox
					id={`batch-errors-${issueNamespace}`}
					title={t((issueNamespace + ".issue") as any, { count: errors.length })}
					info={t((issueNamespace + ".issueInfo") as any)}
					count={errors.length}
					className="border-danger bg-white"
					titleClassName="text-danger"
				>
					{renderIssueList(errors)}
				</IssueBox>
			)}

			{warnings.length > 0 && (
				<IssueBox
					id={`batch-warnings-${issueNamespace}`}
					title={t((issueNamespace + ".warningsTitle") as any, { count: warnings.length })}
					info={t((issueNamespace + ".warningsInfo") as any)}
					count={warnings.length}
					className="border-amber-400 bg-amber-50"
					titleClassName="text-amber-700"
				>
					{renderIssueList(warnings)}
				</IssueBox>
			)}
		</div>
	);
};

export default BatchWarningSummary;

/**
 * One error/warning box. Collapsed by default so a long issue list doesn't push
 * the batch content off screen: the header shows the title with the count and a
 * "Visa alla (n)" toggle; expanding reveals the explanatory text and the list.
 */
const IssueBox: React.FC<{
	id: string;
	title: string;
	info: string;
	count: number;
	className: string;
	titleClassName: string;
	children: React.ReactNode;
}> = ({ id, title, info, count, className, titleClassName, children }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);
	const toggle = () => setExpanded((prev) => !prev);

	return (
		<div className={cn("rounded-md border-2 border-solid p-4", className)}>
			<div className="flex items-center justify-between gap-4">
				<button
					type="button"
					onClick={toggle}
					aria-expanded={expanded}
					aria-controls={id}
					className={cn(
						"m-0 cursor-pointer border-0 bg-transparent p-0 text-left text-lg font-medium",
						titleClassName
					)}
				>
					{title}
				</button>
				<ExpandToggle
					expanded={expanded}
					onToggle={toggle}
					controls={id}
					showLabel={t("batchIssues.showAll", { count })}
					hideLabel={t("batchIssues.hide")}
				/>
			</div>

			{expanded && (
				<div id={id}>
					<p className="mb-3 mt-2">{info}</p>
					{children}
				</div>
			)}
		</div>
	);
};
